import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { RenderIf } from 'components/RenderIf'
import { NotFoundMessage } from 'components/commonStyles'
import { CategoryItem } from 'sections/Menu/Categories/CategoryItem'
import { Sidebar } from 'featureSliced/widgets/Sidebar'

import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useTranslations } from 'featureSliced/shared/hooks/useTranslations'
import { useGetProductPrice } from 'featureSliced/shared/hooks/useGetProductPrice/indexT'
import { useOpenProduct } from 'featureSliced/shared/hooks/openModals/useOpenProductModal'
import {
  ICategory,
  IProduct,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import { TableStatuses } from 'featureSliced/shared/store/reducers/QRMenuTableReducer/types'
import { getTableData } from 'featureSliced/shared/store/reducers/CommonReducer/actionCreator'
import { handleLocalStorage } from 'featureSliced/shared/utils/handleLocalStorage'
import { convertToPrice } from 'featureSliced/shared/utils/convertToPrice'
import { getNavLink } from 'featureSliced/shared/utils/getNavLink'
import { isCategoryAvailableToday } from 'featureSliced/shared/utils/isCategoryAvailableToday'
import { roundValueTo } from 'featureSliced/shared/utils/roundValueTo'

import { WebHeader } from './WebHeader'
import { WebSideColumn } from './WebSideColumn'
import { WebCartPanel } from './WebCartPanel'
import { WebBody, WebMain, WebScreen } from './styled'

type MenuWebProps = {
  categories: ICategory[]
  openAllergensModal: () => void
  editTimeLocation: () => void
  /** true while the viewport is wide enough for the cart to have its own column */
  isWide: boolean
}

const IS_CLICK_COLLECT = process.env.REACT_APP_IS_CLIC === '1'

export const MenuWeb: FC<MenuWebProps> = ({
  categories,
  openAllergensModal,
  editTimeLocation,
  isWide,
}) => {
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { getProductPrice } = useGetProductPrice()
  const openProductModal = useOpenProduct()

  const lines = useMySelector((state) => state.bucket.products)
  const allProducts = useMySelector((state) =>
    state.categories.items.flatMap((cat) => cat.products)
  )
  const isProductsLoaded = useMySelector((state) => state.app.isProductsLoaded)
  const tableStatus = useMySelector((state) => state.qrMenuTable.tableStatus)
  const { Currency } = useMySelector((state) => state.app.companyData)
  const { paymentVariant } = useMySelector((state) => state.app.menu)

  const [isSidebarShowing, setIsSidebarShowing] = useState(false)
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null)

  // The phone layout fetches the table data inside components/Header, which the web
  // branch does not render — without this the QR build's left column could never
  // show the table number (QRM-641).
  const table = searchParams.get('table')
  const { guid, dataEnc } = handleLocalStorage(['guid', 'dataEnc'])
  useEffect(() => {
    if (IS_CLICK_COLLECT) return
    if (!table || !(guid || dataEnc)) return
    dispatch(getTableData({ table }))
  }, [table, guid, dataEnc]) // eslint-disable-line

  // wrapped on purpose: filter would pass the index as the helper's `date` argument
  const visibleCategories = useMemo(
    () =>
      categories?.filter((category) => isCategoryAvailableToday(category)) ||
      [],
    [categories]
  )

  // Re-seed whenever the highlighted category is no longer in the list — filtering,
  // a search or a late catalogue load otherwise leaves the accent on a category the
  // middle column is not showing any more.
  useEffect(() => {
    if (!visibleCategories.length) return
    const stillThere = visibleCategories.some((c) => c.id === activeCategoryId)
    if (!stillThere) setActiveCategoryId(visibleCategories[0].id)
  }, [visibleCategories, activeCategoryId])

  // The phone layout scroll-syncs its category slider through IntersectionObserver
  // (sections/Menu/Categories); the web column needs its own, or the accent only
  // ever moves when someone clicks it.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined
    if (!visibleCategories.length) return undefined

    const sections = visibleCategories
      .map((c) => document.getElementById(`container_category_${c.id}`))
      .filter((el): el is HTMLElement => !!el)
    if (!sections.length) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (!visible.length) return
        // the section closest to the top of the reading area wins
        const topMost = visible.reduce((a, b) =>
          a.boundingClientRect.top <= b.boundingClientRect.top ? a : b
        )
        const id = Number(topMost.target.id.replace('container_category_', ''))
        if (!Number.isNaN(id)) setActiveCategoryId(id)
      },
      { rootMargin: '-90px 0px -70% 0px' }
    )
    sections.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [visibleCategories])

  const total = lines.reduce((acc, line) => {
    const product = allProducts.find((p) => p.id === line.productId)
    const itemPrice = product
      ? getProductPrice(product, product.categoryId || 0).finalPrice
      : line.price
    const extras = Array.isArray(line.menuItems)
      ? line.menuItems.reduce((sum, mi) => sum + mi.count * Number(mi.price), 0)
      : 0
    return acc + (itemPrice + extras) * line.count
  }, 0)

  const formattedTotal = total ? convertToPrice(total) : null
  const cartCount = lines.reduce(
    (acc, { count }) => roundValueTo(acc + count),
    0
  )

  const goToCheckout = () => {
    if (!lines.length) return
    if (IS_CLICK_COLLECT) {
      navigate(getNavLink(searchParams, 'payment'))
      return
    }
    if (
      tableStatus === TableStatuses.BUSY ||
      tableStatus === TableStatuses.UNDEFINED
    ) {
      return
    }
    navigate(
      getNavLink(searchParams, paymentVariant === 2 ? 'order' : 'checkout')
    )
  }

  const scrollToCategory = (id: number) => {
    setActiveCategoryId(id)
    document
      .getElementById(`container_category_${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const checkoutLabel = IS_CLICK_COLLECT
    ? t('buttons.continueToCheckout')
    : paymentVariant === 2
    ? t('order.book')
    : t('buttons.checkout')

  return (
    <WebScreen data-testid="menu-web">
      <Sidebar
        isSidebarShowing={isSidebarShowing}
        setIsSidebarShowing={setIsSidebarShowing}
        openProfileModal={() => undefined}
        openAuthModal={() => undefined}
      />

      <WebHeader
        openAllergensModal={openAllergensModal}
        openSidebar={() => setIsSidebarShowing(true)}
        cartTotal={
          formattedTotal ? `${formattedTotal} ${Currency || ''}` : null
        }
        cartCount={cartCount}
        onCartClick={goToCheckout}
        isCartInHeader={!isWide}
      />

      <WebBody>
        <WebSideColumn
          categories={visibleCategories}
          activeCategoryId={activeCategoryId}
          onCategoryClick={scrollToCategory}
          editTimeLocation={IS_CLICK_COLLECT ? editTimeLocation : undefined}
        />

        <WebMain>
          {visibleCategories.map((category) => (
            <CategoryItem key={category.id} category={category} />
          ))}
          <RenderIf
            condition={
              isProductsLoaded &&
              !visibleCategories.find((el) => el.products?.length)
            }
          >
            <NotFoundMessage>{t('errors.didNotFind')}</NotFoundMessage>
          </RenderIf>
        </WebMain>

        <WebCartPanel
          total={formattedTotal}
          onCheckout={goToCheckout}
          checkoutLabel={checkoutLabel}
          onEditLine={(product: IProduct) =>
            openProductModal(product, product.categoryId || 0)
          }
        />
      </WebBody>
    </WebScreen>
  )
}
